import { v4 as uuidV4 } from 'uuid';

import Observable from 'app/lib/Observable';
import Easing from 'app/lib/Easing';

import Mouse from './Mouse';

export interface IControllerConfig {
  enabledFlip?: boolean
}

export interface IControllerPosition {
  point: {
    x: number,
    y: number,
  },
  rect: {
    width: number,
    height: number,
  },
  percent: {
    horizontal: number,
    vertical: number,
  },
  parent: HTMLElement,
  target: HTMLElement,
}

interface MoveEvent {
  time: Date,
  position?: IControllerPosition
}

export interface IControllerPage {
  id: number,
  rotateY: number,
  zIndex: {
    front: number,
    back: number,
  },
  shadow: number,
  moveEvents: {
    start: MoveEvent | null,
    end: MoveEvent | null,
  },
}

export interface IControllerStyles {
  container: string,
  page: string,
  frontSide: string,
  backSide: string,
  shadow: string,
}

class Controller extends Observable {
  static EVENT = {
    MOVE_STEP: 'MOVE_STEP',
    VISIBLE_UPDATE: 'VISIBLE_UPDATE',
  };

  public readonly uuid: string;

  private readonly containerClass: string | null;

  private readonly styles: IControllerStyles;

  private config: IControllerConfig;

  private startEasing: { break: () => void } | null = null;

  private pages: Array<IControllerPage> = [];

  private targetId: number = -1;

  private isRendering: boolean = false;

  private isPlaying: boolean = false;

  private pageCount: number = 0;

  private currentPage: number = 0;

  private visiblePages: Array<number> = [];

  constructor(containerClass: string, pageCount: number, startPage: number, styles: IControllerStyles, initConfig: IControllerConfig) {
    super();

    const container: HTMLDivElement | null = document.querySelector(`.${containerClass}`);
    let currentConfig = {};
    try {
      currentConfig = JSON.parse(container?.getAttribute('data-config') as string);
    } catch {
      currentConfig = {};
    }

    const currentPage = Number(container?.getAttribute('data-current-page') || startPage);

    this.config = {
      enabledFlip: true,
      ...initConfig,
      ...currentConfig,
    };
    try {
      container?.setAttribute('data-config', JSON.stringify(this.config));
    } catch {
      console.log('error save config');
    }

    this.uuid = uuidV4();
    this.styles = styles;

    this.containerClass = containerClass;

    for (let i = 0; i < pageCount; i += 1) {
      this.pages.push({
        id: i,
        rotateY: currentPage > i ? 180 : 0,
        zIndex: {
          front: (pageCount + 1) - i,
          back: i + 1,
        },
        shadow: 0,
        moveEvents: {
          start: null,
          end: null,
        },
      });
    }
    this.pageCount = pageCount;
    this.currentPage = currentPage;
    this.visiblePages = [currentPage];

    this.addMouseListeners();
    this.startRender();
  }

  public updateConfig = (config: IControllerConfig): this => {
    const container: HTMLDivElement | null = document.querySelector(`.${this.containerClass}`);
    this.config = {
      ...this.config,
      ...config,
    };
    try {
      (container as HTMLDivElement)?.setAttribute('data-config', JSON.stringify(this.config));
    } catch {
      console.log('error save config');
    }
    return this;
  };

  public flipLeft = () => {
    const container: HTMLDivElement | null = document.querySelector(`.${this.containerClass}`);
    if (!this.config.enabledFlip || !container || this.isPlaying || this.targetId > -1 || this.currentPage === this.pageCount - 1) {
      return;
    }
    const page = this.pages[this.currentPage];
    if (page.rotateY > 90) {
      return;
    }

    this.flip(this.currentPage, 1, -1, 400);
  };

  public flipRight = () => {
    const container: HTMLDivElement | null = document.querySelector(`.${this.containerClass}`);
    if (!this.config.enabledFlip || !container || this.isPlaying || this.targetId > -1 || this.currentPage === 0) {
      return;
    }
    const page = this.pages[this.currentPage - 1];
    if (page.rotateY < 90) {
      return;
    }

    this.flip(this.currentPage - 1, -1, 1, 400);
  };

  public getCurrentPage = () => this.currentPage;

  public getVisiblePages = () => {
    return [...this.visiblePages];
  };

  public destroy = () => {
    this.removeAllListeners();
    this.removeMouseListeners();
    this.stopRender();
  };

  public showNext = () => {
    if (this.isPlaying || this.targetId > -1) {
      return;
    }
    this.isPlaying = true;
    this.targetId = this.currentPage;
    let easing = new Easing(Easing.fn.easeInOutSine, 1, 0.98, 100, this.handleStep);
    easing.start(() => {
      setTimeout(() => {
        easing = new Easing(Easing.fn.easeInOutSine, 0.98, 1, 300, this.handleStep);
        easing.start(() => {
          this.targetId = -1;
          this.isPlaying = false;
        });
      }, 100);
    });
  };

  private addMouseListeners = (): void => {
    if (typeof document === 'undefined') {
      return;
    }
    Mouse.addMouseDown(document.body, this.handleStart);
  };

  private removeMouseListeners = (): void => {
    if (typeof document === 'undefined') {
      return;
    }
    Mouse.removeMouseDown(document.body, this.handleStart);
  };

  private handleStart = (event: Event): void => {
    if (!this.config.enabledFlip || typeof document === 'undefined') {
      return;
    }

    const position: IControllerPosition | null = this.eventToPosition(event as MouseEvent | TouchEvent);

    if (!position) {
      return;
    }

    if (this.isPlaying || (position.percent.horizontal > -0.85 && position.percent.horizontal < 0.85) || !position.target) {
      return;
    }

    this.targetId = Number(position.target.getAttribute('data-id'));

    if (this.targetId === this.pageCount - 1) {
      this.targetId = -1;
      return;
    }

    const target: HTMLElement | null = document.querySelector(`.${this.styles.page}[data-id="${this.targetId}"]`);
    if (!target) {
      return;
    }

    target.setAttribute('data-touch', 'in-active');

    Mouse.addMouseMove(document.body, this.handleMove);
    Mouse.addMouseUp(document.body, this.handleEnd);

    const startValue = position.percent.horizontal > 0 ? 1 : -1;
    const endValue = position.percent.horizontal;
    this.startEasing = new Easing(Easing.fn.easeInOutCubic, startValue, endValue, 100, this.handleStep).start();

    this.pages[this.targetId].moveEvents.start = this.newMoveEvent(position);
    this.pages[this.targetId].moveEvents.end = this.newMoveEvent(position);
  };

  private handleMove = (event: Event): void => {
    if (typeof document === 'undefined') {
      return;
    }

    const position: IControllerPosition | null = this.eventToPosition(event as MouseEvent | TouchEvent);

    if (!position) {
      return;
    }

    if (this.targetId === -1 || this.isPlaying) {
      return;
    }

    if (this.startEasing) {
      this.startEasing.break();
    }

    this.handleStep(position.percent.horizontal);
    this.pages[this.targetId].moveEvents.end = this.newMoveEvent(position);
  };

  private handleEnd = (): void => {
    if (typeof document === 'undefined') {
      return;
    }

    Mouse.removeMouseMove(document.body, this.handleMove);
    Mouse.removeMouseUp(document.body, this.handleEnd);

    if (this.targetId === -1) {
      return;
    }

    const target: HTMLElement | null = document.querySelector(`.${this.styles.page}[data-id="${this.targetId}"]`);
    if (target && target.hasAttribute('data-touch')) {
      target.removeAttribute('data-touch');
    }

    if (this.isPlaying) {
      return;
    }

    const page = this.pages[this.targetId];

    const { start, end } = page.moveEvents;
    if (end) {
      end.time = this.newMoveEvent().time;
    }

    const time: number = Number(end?.time) - Number(start?.time);
    const isClick: boolean = Math.abs((end?.position?.percent.horizontal || 0) - (start?.position?.percent.horizontal || 0)) < 0.05;
    const isMoving: boolean = !isClick;
    const isFlipToLeft: boolean = (start?.position?.percent?.horizontal || 0) > (end?.position?.percent?.horizontal || 0);
    const isFlipToRight: boolean = (start?.position?.percent?.horizontal || 0) < (end?.position?.percent?.horizontal || 0);

    const startValue: number = end?.position?.percent?.horizontal || 1;
    let endValue: number = startValue > 0 ? 1 : -1;
    let easingFunction = Easing.fn.easeInOutCubic;
    let duration = 400;

    if (isClick && this.targetId === this.currentPage && time < 300) {
      endValue = -1;
      easingFunction = Easing.fn.easeOutCubic;
      duration = 800;
    } else
    if (isClick && this.targetId !== this.currentPage && time < 300) {
      endValue = 1;
      easingFunction = Easing.fn.easeOutCubic;
      duration = 800;
    } else
    if (isMoving && isFlipToLeft && time < 300) {
      endValue = -1;
      easingFunction = Easing.fn.easeOutCubic;
    } else
    if (isMoving && isFlipToRight && time < 300) {
      endValue = 1;
      easingFunction = Easing.fn.easeOutCubic;
    } else
    if (isMoving && isFlipToLeft && startValue < 0.6) {
      endValue = -1;
      duration = 800;
    } else
    if (isMoving && isFlipToRight && startValue > -0.6) {
      endValue = 1;
      duration = 800;
    }

    this.isPlaying = true;
    const easing = new Easing(easingFunction, startValue, endValue, duration, this.handleStep);
    easing.start(() => {
      this.targetId = -1;
      this.isPlaying = false;
    });
  };

  private startRender = (): void => {
    this.isRendering = true;
    requestAnimationFrame(this.render);
  };

  private stopRender = (): void => {
    this.isRendering = false;
  };

  private render = (): void => {
    if (typeof document === 'undefined') {
      return;
    }
    this.pages.forEach((page, idx) => {
      const element = (document.querySelector(`[data-id="${idx}"]`) as HTMLDivElement);
      if (!element) {
        return;
      }
      const shadow = (element.querySelector(`.${this.styles.shadow}`) as HTMLDivElement);
      if (element) {
        element.style.transform = `rotateY(${-page.rotateY - 0.00001}deg)`;
        element.style.zIndex = page.rotateY <= 90 ? page.zIndex.front.toString() : page.zIndex.back.toString();
        if (page.rotateY > 90 && !element.classList.contains(this.styles.backSide)) {
          element.classList.add(this.styles.backSide);
          element.classList.remove(this.styles.frontSide);
        }
        if (page.rotateY <= 90 && element.classList.contains(this.styles.backSide)) {
          element.classList.add(this.styles.frontSide);
          element.classList.remove(this.styles.backSide);
        }
      }
      if (shadow) {
        shadow.style.opacity = page.shadow.toString();
      }
    });

    if (this.isRendering) {
      requestAnimationFrame(this.render);
    }
  };

  private flip = (pageId: number, from: number, to: number, duration: number) => {
    const container: HTMLDivElement | null = document.querySelector(`.${this.containerClass}`);
    if (!container) {
      return;
    }

    this.targetId = Number(pageId);

    this.isPlaying = true;

    const easing = new Easing(Easing.fn.easeInOutCubic, from, to, duration, this.handleStep);
    easing.start(() => {
      this.targetId = -1;
      this.isPlaying = false;
    });
  };

  private newMoveEvent = (position?: IControllerPosition) => ({
    time: new Date(),
    position,
  });

  private handleStep = (value: number): void => {
    const container: HTMLDivElement | null = document.querySelector(`.${this.containerClass}`);
    const page = this.pages[this.targetId];
    page.rotateY = this.getEagle(value);
    page.shadow = this.getShadow(page.rotateY);
    this.dispatchEvent(Controller.EVENT.MOVE_STEP, {
      pageId: this.targetId,
      targetValue: value,
      currentPage: this.currentPage,
      visiblePages: this.getVisiblePages(),
    });
    let isStateUpdated: boolean = false;
    if (value < 0.9 && value > 0 && this.visiblePages.length === 1) {
      if (this.visiblePages.includes(this.targetId)) {
        this.visiblePages.push(this.targetId + 1);
      }
      if (!this.visiblePages.includes(this.targetId)) {
        this.visiblePages.unshift(this.targetId);
      }
      isStateUpdated = true;
    }
    if (value <= 0 && this.visiblePages.length === 2) {
      this.visiblePages.shift();
      const [currentPage] = this.visiblePages;
      this.currentPage = currentPage;
      container?.setAttribute('data-current-page', currentPage.toString());
      isStateUpdated = true;
    }
    if (value >= 0.9 && this.visiblePages.length === 2) {
      this.visiblePages.pop();
      const [currentPage] = this.visiblePages;
      this.currentPage = currentPage;
      container?.setAttribute('data-current-page', currentPage.toString());
      isStateUpdated = true;
    }
    if (isStateUpdated) {
      this.dispatchEvent(Controller.EVENT.VISIBLE_UPDATE, {
        currentPage: this.currentPage,
        visiblePages: this.getVisiblePages(),
      });
    }
  };

  private getShadow = (eagle: number): number => {
    return 1 - Math.abs((eagle - 90) / 90);
  };

  private getEagle = (x: number): number => {
    // eslint-disable-next-line no-restricted-properties
    const pos = Math.sqrt(1 - Math.pow(Math.abs(x), 1));
    const value = (pos * -90) || 0;
    return (x > 0 ? value : -(180 + value)) * -1;
  };

  private eventToPosition = (event: TouchEvent | MouseEvent): IControllerPosition | null => {
    const object: Touch | MouseEvent = ('touches' in event) ? ((event as TouchEvent).touches[0] as Touch) : (event as MouseEvent);

    const { clientX, clientY, target } = object;
    const parent: HTMLElement | null = document.querySelector(`.${this.styles.container}`);
    if (!parent) {
      return null;
    }

    let page: HTMLElement | null = (target as HTMLElement).closest(`.${this.styles.page}`);

    if (this.currentPage === 0 && !page) {
      return null;
    }

    if (!page) {
      const prevPage: HTMLElement | null = parent.querySelector(`.${this.styles.page}[data-id="${this.currentPage - 1}"]`);
      const currentPage: HTMLElement | null = parent.querySelector(`.${this.styles.page}[data-id="${this.currentPage}"]`);

      const prevPageRect: DOMRect | undefined = prevPage?.getBoundingClientRect();
      const currentPageRect: DOMRect | undefined = currentPage?.getBoundingClientRect();

      if (clientX < (prevPageRect?.x || 0)) {
        page = prevPage;
      }
      if (clientX > (currentPageRect?.x || 0) + (currentPageRect?.width || 0)) {
        page = currentPage;
      }
    }

    const rect = (parent as HTMLElement).getBoundingClientRect();
    const x: number = clientX - rect.x;
    const y: number = clientY - rect.y;

    return {
      point: {
        x,
        y,
      },
      rect: {
        width: rect.width,
        height: rect.height,
      },
      percent: {
        horizontal: x / rect.width,
        vertical: y / rect.height,
      },
      parent: (parent as HTMLElement),
      target: (page as HTMLElement),
    };
  };
}

export default Controller;
